import { Grid } from "@mui/material";
import { useState, useEffect } from "react";
import AddIcon from '@mui/icons-material/Add';
import ApartmentIcon from '@mui/icons-material/Apartment';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { FieldSelect, FieldMultiSelect } from "../../components/selections/fieldInputs";
import SnackbarMessage from "../../components/snackBar/snackBar";
import { addBranchDepartmentThunk, branchDepartmentThunk } from "../../components/general/generalThunk";
import { useDispatch, useSelector } from "react-redux";
import { QR, QrActionButton } from "../qrCode/components/qrUi";

export default function CreateBranchDepartment({ handleClose }) {
    const dispatch = useDispatch();

    const { branches, departments, branchDepartments, status, error } = useSelector((state) => state.general);

    const [state, setState] = useState({
        departmentValue: "",
        selectedBranchValue: [], // nothing selected by default
        filteredBranches: [], // options for MultiSelect
        errors: {},
    });

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    // Handle department selection
    const handleSingleSelectChange = (name, selectedValue) => {
        // Get branch ids already used in this department
        const usedBranchIds = branchDepartments
            .filter((item) => +item.department_id === +selectedValue)
            .map((item) => item.branch_id);

        // Prepare branch options excluding already used
        const remainingBranches = branches
            .filter((branch) => !usedBranchIds.includes(branch.id))
            .map((branch) => ({ label: branch.branch_name, value: branch.id }));

        setState((prev) => ({
            ...prev,
            [name]: selectedValue,
            selectedBranchValue: [], // reset MultiSelect selection
            filteredBranches: remainingBranches,
            errors: { ...prev.errors, [name]: "" },
        }));
    };

    // Handle MultiSelect branch selection
    const handleMultiSelectChange = (name, selectedValues) => {
        setState((prev) => ({
            ...prev,
            [name]: selectedValues,
            errors: { ...prev.errors, [name]: "" },
        }));
    };

    // Submit form
    const handleSubmit = async (e) => {
        e.preventDefault();

        const validations = [
            { valid: !!state.departmentValue, message: "Please Select Department", field: "departmentValue" },
            { valid: state.selectedBranchValue.length > 0, message: "Please Select Branch", field: "selectedBranchValue" },
        ];

        for (let rule of validations) {
            if (!rule.valid) {
                setSnackbar({ open: true, message: rule.message, severity: "error" });
                setState((prev) => ({ ...prev, errors: { ...prev.errors, [rule.field]: rule.message } }));
                return;
            }
        }

        try {
            const result = await dispatch(addBranchDepartmentThunk({
                credentials: {
                    branch_ids: state.selectedBranchValue.map((item) => item.value).toString(),
                    department_id: state.departmentValue,
                }
            }));

            if (result.meta.requestStatus === "fulfilled") {
                setSnackbar({ open: true, message: result.payload.message, severity: "success" });
                dispatch(branchDepartmentThunk({ type: "getBranchDepartment" }));
                handleClose();
            } else {
                setSnackbar({ open: true, message: result.payload, severity: "error" });
            }
        } catch {
            setSnackbar({ open: true, message: "Server error. Please try again.", severity: "error" });
        }
    };

    // Display API error in snackbar
    useEffect(() => {
        if (error) {
            setSnackbar({ open: true, message: error, severity: "error" });
        }
    }, [error]);

    return (
        <div>
            <Grid container spacing={2.5} alignItems="flex-end">
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <FieldSelect
                        name="departmentValue"
                        label="Department"
                        placeholder="Select department"
                        icon={<ApartmentIcon />}
                        required
                        options={departments.map((item) => ({ value: item.id, label: item.department_name }))}
                        value={state.departmentValue}
                        onChange={handleSingleSelectChange}
                        error={state.errors?.departmentValue}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <FieldMultiSelect
                        name="selectedBranchValue"
                        label="Branch"
                        placeholder={state.departmentValue ? "Select branches" : "Select a department first"}
                        icon={<AccountTreeOutlinedIcon />}
                        required
                        options={state.filteredBranches}
                        value={state.selectedBranchValue}
                        onChange={handleMultiSelectChange}
                        error={state.errors?.selectedBranchValue}
                        disabled={!state.departmentValue} // disabled until department selected
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={{ display: "flex", justifyContent: { xs: "flex-end", md: "flex-start" } }}>
                    <QrActionButton
                        variant="solid"
                        color={QR.main}
                        gradientTo={QR.light}
                        icon={<AddIcon sx={{ fontSize: 17 }} />}
                        label="Create"
                        loading={status === "loading"}
                        loadingLabel="Submitting.."
                        onClick={handleSubmit}
                    />
                </Grid>
            </Grid>


            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            />
        </div>
    );
}
